import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Trophy, ChevronLeft, BarChart2, SkipForward, Swords } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface ContestNavigationProps {
  currentPairingIndex: number;
  totalPairings: number;
  onPrevious: () => void;
  onNext: () => void;
  onReturn: () => void;
  onViewSummary?: () => void;
  disableNext?: boolean;
  isContestCompleted?: boolean;
  isViewingSummary?: boolean;
  currentRound?: string;
  isRoundCompleted?: boolean;
}

const ContestNavigation = ({
  currentPairingIndex,
  totalPairings,
  onPrevious,
  onNext,
  onReturn,
  onViewSummary,
  disableNext = false,
  isContestCompleted = false,
  isViewingSummary = false,
  currentRound = "Initial Round",
  isRoundCompleted = false,
}: ContestNavigationProps) => {
  // Calculate the round-specific total pairings and adjusted current index
  const getRoundTotals = () => {
    // Initial Round (indices 0-3): 4 pairings
    // Semifinal Round (indices 4-5): 2 pairings
    // Final Round (index 6): No progress bar
    
    if (currentPairingIndex <= 3) {
      // Initial Round
      return {
        roundTotal: 4,
        roundIndex: currentPairingIndex,
        shouldShowProgress: true
      };
    } else if (currentPairingIndex <= 5) {
      // Semifinal Round
      return {
        roundTotal: 2,
        roundIndex: currentPairingIndex - 4, // 0-based index within semifinals
        shouldShowProgress: true
      };
    } else {
      // Final Round - no progress thermometer
      return {
        roundTotal: 1,
        roundIndex: 0,
        shouldShowProgress: false
      };
    }
  };
  
  const { roundTotal, roundIndex, shouldShowProgress } = getRoundTotals();
  
  // Calculate progress percentage based on the current round
  const progressPercentage = shouldShowProgress 
    ? Math.round(((roundIndex + 1) / roundTotal) * 100)
    : 100;
  
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-6">
        <Button 
          variant="outline" 
          className="flex items-center gap-2" 
          onClick={onReturn}
          size="sm"
          disabled={isRoundCompleted && !isViewingSummary}
        >
          <ChevronLeft className="h-4 w-4" />
          {isViewingSummary && isContestCompleted ? "Go Again!" : "Abandon progress and start a new match"}
        </Button>
        
        <div className="flex items-center">
          <Trophy className="h-5 w-5 text-amber-500 mr-2" />
          <span className="font-medium">Deathmatch Progress</span>
        </div>
      </div>
      
      {shouldShowProgress && (
        <div className="mb-4">
          <Progress value={progressPercentage} className="h-2" />
        </div>
      )}

      <div className="flex justify-between items-center mt-4">
        {!isViewingSummary ? (
          <>
            <Button
              variant="outline"
              onClick={onPrevious}
              disabled={currentPairingIndex === 0 || isRoundCompleted}
              className="flex items-center gap-2"
              size="lg"
            >
              <ArrowLeft className="h-4 w-4" />
              Previous
            </Button>
            
            <div className="text-center">
              <span className="text-lg font-poetry">
                Pairing {roundIndex + 1} of {roundTotal}
              </span>
              {isRoundCompleted && (
                <div className="text-sm text-green-600 mt-1">Round completed</div>
              )}
            </div>
            
            {isContestCompleted && currentPairingIndex === totalPairings - 1 ? (
              <Button
                variant="default"
                onClick={onViewSummary}
                className="flex items-center gap-2"
                size="lg"
              >
                Finish the Deathmatch!
                <Swords className="h-4 w-4" />
              </Button>
            ) : isRoundCompleted && currentPairingIndex < totalPairings - 1 ? (
              <Button
                variant="default" 
                onClick={onNext}
                className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600"
                size="lg"
              >
                Next Round
                <SkipForward className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={onNext}
                disabled={currentPairingIndex === totalPairings - 1 || disableNext}
                className="flex items-center gap-2"
                size="lg"
              >
                Next
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </>
        ) : (
          <>
            {!isContestCompleted && (
              <Button
                variant="outline"
                onClick={onPrevious}
                className="flex items-center gap-2"
                size="lg"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Contest
              </Button>
            )}
            
            <div className="text-center">
              {!isContestCompleted && (
                <span className="text-lg font-poetry">
                  Contest Summary
                </span>
              )}
            </div>
            
            <div className="w-[120px]">
              {/* Empty div for layout balance */}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ContestNavigation;
